import { Injectable } from '@angular/core';
import { PopoverController } from '@ionic/angular';

import { SearchablePopoverComponent } from 'src/app/components/searchable-popover/searchable-popover.component';
import {
  CONVERSATION_PRIORITY_VALUES,
  CONVERSATION_STATUS,
  DATE_OPTIONS,
  DEFAULT_PRIORITY_TOKENS,
  ESCALATION_STATUSES,
  ESCALATION_STATUS_LABELS,
  EXTERNAL_TASK_STATE,
  PROJECT_MEMBER_ROLES,
  PROJECT_MEMBER_ROLE_NAMES,
  ProjectMemberRole,
  STATUS_COLUMN_FORMAT_MAP,
  SUPPORT_STEP_ASSIGNEE_TYPES,
  SUPPORT_STEP_KEYWORDS,
  routes,
} from 'src/app/constants';
import { PATHS, getInboxSubpages } from 'src/app/cx-home/cx-home.component';
import { CURRENT_USER_SENTINEL } from 'src/app/utils/filter-utils';
import { environment } from 'src/environments/environment';
import {
  Automation,
  Conversation,
  Customer,
  EscalationType,
  ExternalCrmAccount,
  ListResponse,
  Profile,
  SavedFilter,
  SavedReport,
  SlackGroup,
  Tag,
  Template,
  TicketType,
  SubStatus,
  WhereClause,
  KnowledgeBaseScrapeJob,
  CustomField,
  CollaboratorType,
  CUSTOM_FIELD_TYPES,
  PriorityTokens,
} from 'src/models';
import { Account } from 'src/models/accounts';
import { Entity } from 'src/models/entities';
import { Project } from 'src/models/projects';
import { UserGroup } from 'src/models/user-groups';
import { TENANT_USER_ROLES, TENANT_USER_ROLE_NAMES } from 'src/models/users';
import { ApiService } from 'src/services/api.service';
import { ConversationsService } from 'src/services/conversations.service';
import { CustomersService } from 'src/services/customers.service';
import { KnowledgeBaseScrapeJobsService } from 'src/services/knowledge-base-scrape-jobs.service';
import { SlackUserGroupsService } from 'src/services/slack-user-groups.service';
import { TagsService } from 'src/services/tags.service';
import { UserGroupsService } from 'src/services/user-groups.service';
import { UsersService } from 'src/services/users.service';

import { AccountsService } from './accounts.service';
import { AuthService } from './auth.service';
import { AutomationsService } from './automations.service';
import { CollaboratorTypesService } from './collaborator-types.service';
import { CustomFieldsService } from './custom-fields.service';
import { EmojisService } from './emojis.service';
import { EscalationTypesService } from './escalation-types.service';
import { ProjectsService } from './projects.service';
import { ServiceAccountsService } from './service-accounts.service';
import { SlackService } from './slack.service';
import { TicketTypesService } from './ticket-types.service';
import { TokenService } from './token.service';

type PopoverSide = 'top' | 'bottom' | 'left' | 'right';

export const STATUS_OPTIONS = [
  {
    id: CONVERSATION_STATUS.OPEN,
    title: 'Needs response',
    icon: STATUS_COLUMN_FORMAT_MAP.open.icon,
    iconClass: STATUS_COLUMN_FORMAT_MAP.open.iconClass,
  },
  {
    id: CONVERSATION_STATUS.IN_PROGRESS,
    title: 'In progress',
    icon: STATUS_COLUMN_FORMAT_MAP.in_progress.icon,
    iconClass: STATUS_COLUMN_FORMAT_MAP.in_progress.iconClass,
  },
  {
    id: CONVERSATION_STATUS.ON_HOLD,
    title: 'On hold',
    icon: STATUS_COLUMN_FORMAT_MAP.on_hold.icon,
    iconClass: STATUS_COLUMN_FORMAT_MAP.on_hold.iconClass,
  },
  {
    id: CONVERSATION_STATUS.CLOSED,
    title: 'Closed',
    icon: STATUS_COLUMN_FORMAT_MAP.closed.icon,
    iconClass: STATUS_COLUMN_FORMAT_MAP.closed.iconClass,
  },
  {
    id: CONVERSATION_STATUS.INTAKE,
    title: 'Intake',
  },
];

export const ESCALATION_STATUS_OPTIONS = [
  {
    id: ESCALATION_STATUSES.PENDING,
    icon: 'custom-open',
    title: ESCALATION_STATUS_LABELS[ESCALATION_STATUSES.PENDING],
  },
  {
    id: ESCALATION_STATUSES.ACKNOWLEDGED,
    icon: 'custom-in-progress',
    title: ESCALATION_STATUS_LABELS[ESCALATION_STATUSES.ACKNOWLEDGED],
  },
  {
    id: ESCALATION_STATUSES.RESOLVED,
    icon: 'custom-closed',
    title: ESCALATION_STATUS_LABELS[ESCALATION_STATUSES.RESOLVED],
  },
];

export const EXTERNAL_TASK_FILTER_OPTIONS = [
  {
    id: EXTERNAL_TASK_STATE.ALL_TASKS_COMPLETE,
    title: 'All Tasks Complete',
  },
  {
    id: EXTERNAL_TASK_STATE.HAS_INCOMPLETE_TASKS,
    title: 'Some Tasks Incomplete',
  },
];

export const PROJECT_MEMBER_ROLE_OPTIONS = [
  {
    id: PROJECT_MEMBER_ROLES.OWNER,
    title: PROJECT_MEMBER_ROLE_NAMES[PROJECT_MEMBER_ROLES.OWNER],
  },
  {
    id: PROJECT_MEMBER_ROLES.AGENT,
    title: PROJECT_MEMBER_ROLE_NAMES[PROJECT_MEMBER_ROLES.AGENT],
  },
  {
    id: PROJECT_MEMBER_ROLES.STANDARD,
    title: PROJECT_MEMBER_ROLE_NAMES[PROJECT_MEMBER_ROLES.STANDARD],
  },
];

export const TENANT_USER_ROLE_OPTIONS = [
  {
    id: TENANT_USER_ROLES.OWNER,
    title: TENANT_USER_ROLE_NAMES[TENANT_USER_ROLES.OWNER],
  },
  {
    id: TENANT_USER_ROLES.ADMIN,
    title: TENANT_USER_ROLE_NAMES[TENANT_USER_ROLES.ADMIN],
  },
  {
    id: TENANT_USER_ROLES.STANDARD,
    title: TENANT_USER_ROLE_NAMES[TENANT_USER_ROLES.STANDARD],
  },
];

const NONE_TEXT = 'None';

function formatUserName(user: Partial<Profile>, connectedTeams: any[]) {
  const name = user.name?.trim() || user.email?.trim() || 'No Name';
  return `${name}${connectedTeams?.length > 1 && user.slackTeam?.name ? ` (${user.slackTeam.name})` : ''}`;
}

// Helper function to map sourceType to icon name
function mapSourceTypeToIconName(sourceType: string | undefined): string | undefined {
  switch (sourceType) {
    case 'notion':
      return 'custom-notion';
    case 'confluence':
      return 'custom-confluence';
    case 'google-drive':
      return 'custom-google-drive';
    case 'web':
      return 'globe-outline';
    case 'wiki-js':
      return 'custom-wiki-js';
    case 'coda':
      return 'custom-coda';
    case 'sharepoint-online':
      return 'custom-microsoft-sharepoint';
    default:
      return undefined;
  }
}

export const GROUP_MEMBER_SIGIL = '__GROUP_MEMBER__';

@Injectable({
  providedIn: 'root',
})
export class SearchablePopoverService {
  constructor(
    private popoverCtrl: PopoverController,
    private usersService: UsersService,
    private userGroupsService: UserGroupsService,
    private slackUserGroupsService: SlackUserGroupsService,
    private tagsService: TagsService,
    private apiService: ApiService,
    private customersService: CustomersService,
    private conversationsService: ConversationsService,
    private customFieldsService: CustomFieldsService,
    private collaboratorTypesService: CollaboratorTypesService,
    private ticketTypesService: TicketTypesService,
    private automationsService: AutomationsService,
    private slackService: SlackService,
    private projectsService: ProjectsService,
    private serviceAccountsService: ServiceAccountsService,
    private authService: AuthService,
    private knowledgeBaseScrapeJobsService: KnowledgeBaseScrapeJobsService,
    private tokenService: TokenService,
    private emojiService: EmojisService,
    private escalationTypesService: EscalationTypesService,
    private accountsService: AccountsService,
  ) {}

  public async showPopover({
    selectedItemId,
    callback,
    loadData,
    showImages,
    showSearch,
    placeholderLink,
    noneOptionLabel,
    dynamicCreationCallback,
    dynamicCreationCallbackLabel,
    event,
    side,
    componentView,
    initialSearchText,
    initialItems,
  }: {
    callback: (id: string, user: any, object: any) => void;
    event: any;
    selectedItemId?: string;
    loadData: (searchText: string) => Promise<any[]>;
    showImages?: boolean;
    showSearch?: boolean;
    placeholderLink?: string;
    noneOptionLabel?: string;
    dynamicCreationCallback?: (searchText: string) => void;
    dynamicCreationCallbackLabel?: string;
    side?: PopoverSide;
    componentView?: string;
    initialSearchText?: string;
    initialItems?: any[];
  }) {
    const popover = await this.popoverCtrl.create({
      component: SearchablePopoverComponent,
      cssClass: 'searchable-popover',
      componentProps: {
        selectedItemId,
        showImages,
        showSearch,
        placeholderLink,
        dynamicCreationCallback,
        dynamicCreationCallbackLabel,
        callback,
        loadData,
        componentView,
        initialSearchText,
        items: initialItems,
      },
      showBackdrop: false,
      side,
      event,
    });

    popover.present();
  }

  public async users({
    callback,
    event,
    selectedUserId,
    side,
    showNoneOption = true,
    showAssignToMeText,
    showCurrentUserOption = false,
    includeExternalUsers = false,
    includeEmailOnlyUsers = true,
    where,
  }: {
    callback: (id: string, name: string, user: any) => void;
    event: any;
    selectedUserId?: string;
    side?: PopoverSide;
    showNoneOption?: boolean;
    showAssignToMeText?: string;
    showCurrentUserOption?: boolean;
    includeExternalUsers?: boolean;
    includeEmailOnlyUsers?: boolean;
    where?: WhereClause[];
  }) {
    let assignToMeButton = undefined;

    if (showAssignToMeText?.length) {
      assignToMeButton = {
        title: showAssignToMeText,
        id: this.authService.userId,
        image: this.authService.userPhoto,
        object: this.authService.user,
      };
    }

    this.showPopover({
      event,
      showImages: true,
      showSearch: true,
      side,
      selectedItemId: selectedUserId,
      callback,
      loadData: async (searchText: string) => {
        const fullWhere: WhereClause[] = [
          ...(where ?? []),
          {
            field: 'isAutoAssignable',
            operator: '!=',
            value: false,
          },
          {
            field: 'status',
            operator: '==',
            value: 'active',
          },
          {
            field: 'name',
            operator: '!=',
            value: null,
          },
        ];

        // Determine which fields to search based on search text
        let searchFields: string[] = [];

        if (searchText?.trim().length) {
          searchFields.push('name');

          if (searchText.includes('@')) {
            searchFields.push('email');
          }
        }

        const result = await this.usersService.list({
          where: fullWhere,
          limit: 100,
          order: ['name'],
          descending: false,
          includeExternalUsers,
          includeEmailOnlyUsers,
          searchText: searchText?.trim() || undefined,
          searchFields: searchFields.length > 0 ? searchFields : undefined,
        });

        const data = [];

        if (showNoneOption && (!searchText?.length || NONE_TEXT.toLowerCase().includes(searchText.toLowerCase()))) {
          data.push({
            title: NONE_TEXT,
            id: null,
            image: '',
            object: {},
          });
        }

        if (assignToMeButton) {
          data.push(assignToMeButton);
        }

        if (showCurrentUserOption) {
          data.push({
            id: CURRENT_USER_SENTINEL,
            title: 'Current User',
            icon: 'person-circle-outline',
            image: undefined,
            object: { id: CURRENT_USER_SENTINEL, name: 'Current User' },
          });
        }

        const connectedTeams = await this.slackService.getConnectedTeams();

        // Client-side filtering and display formatting for email matches
        const searchTerm = searchText?.toLowerCase().trim() || '';

        result.data.forEach((user) => {
          // Check if search text matches email for display formatting
          const emailMatched = searchTerm && user.email?.toLowerCase().includes(searchTerm);
          const nameMatched = searchTerm && user.name?.toLowerCase().includes(searchTerm);

          // If search matches email and not name, show "Name (email)"
          const formattedUserName = formatUserName(user, connectedTeams);
          const displayTitle =
            emailMatched && !nameMatched && user.email ? `${formattedUserName} (${user.email})` : formattedUserName;

          data.push({
            title: displayTitle,
            id: user.id,
            image: user.photo,
            object: user,
          });
        });

        return data;
      },
    });
  }

  public async assignees({
    callback,
    event,
    selectedId,
    showPrimaryRepOption,
    showCreatedByUserOption,
    includeNonAgents = false,
    onlyMentionable = false,
    showNoneOption,
    includeGroups = true,
    showAutoAssignFromGroupOption = false,
    side,
    userGroupId,
  }: {
    callback: (id: string, name: string, user: any) => void;
    event: any;
    selectedId?: string;
    showPrimaryRepOption: boolean;
    showCreatedByUserOption: boolean;
    includeNonAgents?: boolean;
    onlyMentionable?: boolean;
    showNoneOption: boolean;
    includeGroups?: boolean;
    side?: PopoverSide;
    showAutoAssignFromGroupOption?: boolean;
    userGroupId?: string;
  }) {
    this.showPopover({
      event,
      showImages: true,
      showSearch: true,
      side,
      selectedItemId: selectedId,
      callback,
      loadData: async (searchText: string) => {
        const where: WhereClause[] = [
          {
            field: 'status',
            operator: '==',
            value: 'active',
          },
          {
            field: 'name',
            operator: '!=',
            value: null,
          },
        ];

        if (!includeNonAgents) {
          where.push({
            field: 'isAutoAssignable',
            operator: '!=',
            value: false,
          });
        }

        if (onlyMentionable) {
          where.push({
            field: 'slackIds',
            operator: 'notIn',
            value: [null, []],
          });
        }

        if (userGroupId) {
          where.push({
            field: 'userGroups.id',
            operator: 'contains',
            value: [userGroupId],
          });
        }

        const userGroupWhere: WhereClause[] = [
          {
            field: 'name',
            operator: '!=',
            value: null,
          },
          {
            field: 'isExternal',
            operator: '==',
            value: false,
          },
        ];

        if (onlyMentionable) {
          userGroupWhere.push({
            field: 'slackUserGroup.slackId',
            operator: '!=',
            value: null,
          });
        }

        if (searchText?.length) {
          where.push({
            field: 'name',
            operator: 'like',
            value: `%${searchText}%`,
          });
          userGroupWhere.push({
            field: 'name',
            operator: 'like',
            value: `%${searchText}%`,
          });
        }

        const [users, userGroups] = await Promise.all([
          this.usersService.list({
            where,
            limit: 100,
            order: ['name'],
            descending: false,
          }),
          includeGroups
            ? this.userGroupsService.list({
                limit: 100,
                order: ['name'],
                descending: false,
                where: userGroupWhere,
              })
            : { data: [] },
        ]);

        const data = [];
        const noneText = 'No one';
        const primaryRepText = 'Primary support rep';
        const secondaryRepText = 'Secondary support rep';
        const assigneeManagerText = "Assignee's manager";
        const submitterManagerText = "Submitter's manager";
        const userCreatedCustomerText = 'User who connected the channel';
        const autoAssignFromGroupText = 'Auto assign from group';

        if (showNoneOption && (!searchText?.length || noneText.toLowerCase().includes(searchText.toLowerCase()))) {
          data.push({
            title: noneText,
            id: null,
            image: environment.assetsBaseUrl + '/imgs/1x1.png',
            object: {},
          });
        }

        if (
          showPrimaryRepOption &&
          (!searchText?.length ||
            primaryRepText.toLowerCase().includes(searchText.toLowerCase()) ||
            secondaryRepText.toLowerCase().includes(searchText.toLowerCase()))
        ) {
          data.push(
            {
              title: primaryRepText,
              id: SUPPORT_STEP_KEYWORDS.PRIMARY_SUPPORT_ASSIGNEE,
              image: environment.assetsBaseUrl + '/imgs/1x1.png',
              object: {},
            },
            {
              title: secondaryRepText,
              id: SUPPORT_STEP_KEYWORDS.SECONDARY_SUPPORT_ASSIGNEE,
              image: environment.assetsBaseUrl + '/imgs/1x1.png',
              object: {},
            },
            {
              title: assigneeManagerText,
              id: SUPPORT_STEP_KEYWORDS.ASSIGNEE_MANAGER,
              image: environment.assetsBaseUrl + '/imgs/1x1.png',
              object: {},
            },
            {
              title: submitterManagerText,
              id: SUPPORT_STEP_KEYWORDS.SUBMITTER_MANAGER,
              image: environment.assetsBaseUrl + '/imgs/1x1.png',
              object: {},
            },
          );
        }

        if (
          showAutoAssignFromGroupOption &&
          (!searchText?.length || autoAssignFromGroupText.toLowerCase().includes(searchText.toLowerCase()))
        ) {
          data.push({
            title: autoAssignFromGroupText,
            id: GROUP_MEMBER_SIGIL,
            image: environment.assetsBaseUrl + '/imgs/1x1.png',
            object: {},
          });
        }

        if (
          showCreatedByUserOption &&
          (!searchText?.length || userCreatedCustomerText.toLowerCase().includes(searchText.toLowerCase()))
        ) {
          data.push({
            title: userCreatedCustomerText,
            id: SUPPORT_STEP_KEYWORDS.CREATED_BY_USER,
            image: environment.assetsBaseUrl + '/imgs/1x1.png',
            object: {},
          });
        }

        userGroups.data.forEach((group) => {
          data.push({
            id: group.id,
            title: `${group.name} (Group)`,
            image: '',
            object: {
              type: SUPPORT_STEP_ASSIGNEE_TYPES.TEAM,
              ...group,
            },
          });
        });

        const connectedTeams = await this.slackService.getConnectedTeams();

        users.data.forEach((user) => {
          data.push({
            title: formatUserName(user, connectedTeams),
            id: user.id,
            image: user.photo,
            object: {
              type: SUPPORT_STEP_ASSIGNEE_TYPES.USER,
              ...user,
            },
          });
        });

        return data;
      },
    });
  }

  public async slackEntities({
    callback,
    event,
    selectedId,
    showPrimaryRepOption,
    showCreatedByUserOption,
    showNoneOption,
    side,
  }: {
    callback: (id: string, name: string, user: any) => void;
    event: any;
    selectedId?: string;
    showPrimaryRepOption: boolean;
    showCreatedByUserOption: boolean;
    includeNonAgents?: boolean;
    onlyMentionable?: boolean;
    showNoneOption: boolean;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      showImages: true,
      showSearch: true,
      side,
      selectedItemId: selectedId,
      callback,
      loadData: async (searchText: string) => {
        const where: WhereClause[] = [
          {
            field: 'status',
            operator: '==',
            value: 'active',
          },
          {
            field: 'name',
            operator: '!=',
            value: null,
          },
          {
            field: 'slackIds',
            operator: 'notIn',
            value: [null, []],
          },
        ];

        const slackUserGroupWhere: WhereClause[] = [
          {
            field: 'name',
            operator: '!=',
            value: null,
          },
          {
            field: 'slackId',
            operator: '!=',
            value: null,
          },
        ];

        if (searchText?.length) {
          where.push({
            field: 'name',
            operator: 'like',
            value: `%${searchText}%`,
          });
          slackUserGroupWhere.push({
            field: 'name',
            operator: 'like',
            value: `%${searchText}%`,
          });
        }

        const [users, slackUserGroups] = await Promise.all([
          this.usersService.list({
            where,
            limit: 100,
            order: ['name'],
            descending: false,
          }),
          this.slackUserGroupsService.list({
            limit: 100,
            order: ['name'],
            descending: false,
            where: slackUserGroupWhere,
          }),
        ]);

        const data = [];
        const noneText = 'No one';
        const primaryRepText = 'Primary support rep';
        const secondaryRepText = 'Secondary support rep';
        const assigneeManagerText = "Assignee's manager";
        const submitterManagerText = "Submitter's manager";
        const userCreatedCustomerText = 'User who connected the channel';

        if (showNoneOption && (!searchText?.length || noneText.toLowerCase().includes(searchText.toLowerCase()))) {
          data.push({
            title: noneText,
            id: null,
            image: environment.assetsBaseUrl + '/imgs/1x1.png',
            object: {},
          });
        }

        if (
          showPrimaryRepOption &&
          (!searchText?.length ||
            primaryRepText.toLowerCase().includes(searchText.toLowerCase()) ||
            secondaryRepText.toLowerCase().includes(searchText.toLowerCase()))
        ) {
          data.push(
            {
              title: primaryRepText,
              id: SUPPORT_STEP_KEYWORDS.PRIMARY_SUPPORT_ASSIGNEE,
              image: environment.assetsBaseUrl + '/imgs/1x1.png',
              object: {},
            },
            {
              title: secondaryRepText,
              id: SUPPORT_STEP_KEYWORDS.SECONDARY_SUPPORT_ASSIGNEE,
              image: environment.assetsBaseUrl + '/imgs/1x1.png',
              object: {},
            },
            {
              title: assigneeManagerText,
              id: SUPPORT_STEP_KEYWORDS.ASSIGNEE_MANAGER,
              image: environment.assetsBaseUrl + '/imgs/1x1.png',
              object: {},
            },
            {
              title: submitterManagerText,
              id: SUPPORT_STEP_KEYWORDS.SUBMITTER_MANAGER,
              image: environment.assetsBaseUrl + '/imgs/1x1.png',
              object: {},
            },
          );
        }

        if (
          showCreatedByUserOption &&
          (!searchText?.length || userCreatedCustomerText.toLowerCase().includes(searchText.toLowerCase()))
        ) {
          data.push({
            title: userCreatedCustomerText,
            id: SUPPORT_STEP_KEYWORDS.CREATED_BY_USER,
            image: environment.assetsBaseUrl + '/imgs/1x1.png',
            object: {},
          });
        }

        slackUserGroups.data.forEach((group) => {
          data.push({
            id: group.id,
            title: `${group.name} (Group)`,
            image: '',
            object: {
              type: SUPPORT_STEP_ASSIGNEE_TYPES.TEAM,
              ...group,
            },
          });
        });

        const connectedTeams = await this.slackService.getConnectedTeams();

        users.data.forEach((user) => {
          data.push({
            title: formatUserName(user, connectedTeams),
            id: user.id,
            image: user.photo,
            object: user,
          });
        });

        return data;
      },
    });
  }

  public async tags({
    callback,
    event,
    side,
    projectId,
  }: {
    callback: (id: string, name: any, object: any) => void;
    event: any;
    side?: PopoverSide;
    projectId?: string | null;
  }) {
    this.showPopover({
      event,
      showImages: false,
      showSearch: true,
      placeholderLink: `/${routes.DASHBOARD}/tags`,
      dynamicCreationCallback: async (text: string) => {
        const tag = await this.tagsService.create({
          name: text,
          description: '',
          autoTaggingEnabled: false,
        });
        callback(tag.id, text, tag);
      },
      callback,
      side,
      loadData: async (searchText: string) => {
        const where: WhereClause[] = [];

        if (searchText?.length) {
          where.push({
            field: 'name',
            operator: 'like',
            value: `%${searchText}%`,
          });
        }

        if (projectId) {
          where.push({
            field: 'projectId',
            operator: 'in',
            value: [projectId, null],
          });
        } else if (projectId === null) {
          where.push({
            field: 'projectId',
            operator: '==',
            value: null,
          });
        }

        const tagsResponse = await this.tagsService.list({
          where,
          limit: 100,
          order: ['name'],
          descending: false,
        });

        return tagsResponse.data.map((tag: Tag) => {
          return {
            title: tag.name,
            id: tag.id,
            object: tag,
          };
        });
      },
    });
  }

  public async customers({
    callback,
    event,
    selectedCustomerId,
    side,
    type,
    projectId,
    excludeIds,
    showSlackChannelName = false,
  }: {
    callback: (id: string, customerName: string, customer: any) => void;
    event: any;
    selectedCustomerId?: string;
    side?: PopoverSide;
    type?: string;
    projectId?: string;
    excludeIds?: string[];
    showSlackChannelName?: boolean;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedCustomerId,
      showImages: false,
      showSearch: true,
      side,
      callback,
      loadData: async (searchText: string) => {
        const where: WhereClause[] = [];

        if (projectId) {
          where.push({
            field: 'projectId',
            operator: '==',
            value: projectId,
          });
        }

        if (searchText?.length) {
          where.push({
            field: 'name',
            operator: 'like',
            value: `%${searchText}%`,
          });
        }

        if (type) {
          where.push({
            field: 'type',
            operator: '==',
            value: type,
          });
        }

        if (excludeIds?.length) {
          where.push({
            field: 'id',
            operator: 'notIn',
            value: excludeIds,
          });
        }

        const response = await this.customersService.list({
          where,
          limit: 100,
          order: ['name'],
          descending: false,
        });

        return response.data.map((customer: Customer) => {
          return {
            title:
              showSlackChannelName && customer.slackChannel?.name
                ? `${customer.name} (#${customer.slackChannel?.name})`
                : customer.name,
            id: customer.id,
            object: customer,
          };
        });
      },
    });
  }

  public async statuses({
    callback,
    event,
    selectedStatusId,
    showIntakeStatus = false,
    side,
  }: {
    callback: (id: string, user: any) => void;
    event: any;
    selectedStatusId?: string;
    showIntakeStatus?: boolean;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedStatusId,
      showImages: false,
      showSearch: false,
      callback,
      side,
      loadData: async () => {
        return STATUS_OPTIONS.filter((status) => showIntakeStatus || status.id !== CONVERSATION_STATUS.INTAKE);
      },
    });
  }

  public async escalationStatuses({
    callback,
    event,
    selectedStatusId,
    side,
  }: {
    callback: (id: string, user: any) => void;
    event: any;
    selectedStatusId?: string;
    showIntakeStatus?: boolean;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedStatusId,
      callback,
      loadData: async () => ESCALATION_STATUS_OPTIONS,
      showSearch: false,
      side,
    });
  }

  public async priorities({
    callback,
    event,
    selectedPriority,
    customerId,
    projectId,
    side,
  }: {
    callback: (id: string, title: string, object: any) => void;
    event: any;
    selectedPriority?: string;
    customerId?: string;
    projectId?: string;
    side?: PopoverSide;
  }) {
    const priorityTokenMap: Record<string, PriorityTokens> = (
      await this.tokenService.getResolvedTokens(['PRIORITY_LABEL_MAP'], customerId, projectId)
    )['PRIORITY_LABEL_MAP'];

    // ensure emojis are loaded
    await this.emojiService.loadEmojis();

    const priorityKeys = Object.keys(CONVERSATION_PRIORITY_VALUES);

    // Step 1: Get all emoji strings/IDs that need resolution
    const emojiIdentifiersToResolve: string[] = priorityKeys.map((key) => {
      // fallback to defaults here (they are native emojis!)
      return priorityTokenMap[key.toUpperCase()]?.EMOJI || DEFAULT_PRIORITY_TOKENS[key.toUpperCase()].EMOJI;
    });

    // Step 2: Resolve all of them in parallel
    const resolvedEmojis = await Promise.all(
      emojiIdentifiersToResolve.map((identifier) => {
        if (typeof identifier === 'string' && (identifier.startsWith(':') || /^[a-zA-Z0-9_+-]+$/.test(identifier))) {
          // If it looks like an ID (colon-wrapped or plain word for ID, e.g. "smile")
          // Pass a clean ID (remove colons if present)
          return this.emojiService.resolveEmojiToDisplay(identifier.replace(/:/g, ''));
        }
        // If it's not a string or doesn't look like an ID, assume it's already resolved (e.g., native emoji "😄")
        // or doesn't need async resolution.
        return Promise.resolve(identifier);
      }),
    );

    // Step 3: Now map to priorityOptions using the resolved emojis
    const priorityOptions = priorityKeys.map((key, index) => {
      const value = CONVERSATION_PRIORITY_VALUES[key];
      const label = priorityTokenMap[key.toUpperCase()]?.LABEL || DEFAULT_PRIORITY_TOKENS[key.toUpperCase()].LABEL;
      const displayEmoji = resolvedEmojis[index];
      const description =
        priorityTokenMap[key.toUpperCase()]?.DESCRIPTION || DEFAULT_PRIORITY_TOKENS[key.toUpperCase()].DESCRIPTION;

      return {
        id: value,
        emoji: displayEmoji,
        title: `${label}${description ? ` - ${description}` : ''}`,
      };
    });

    this.showPopover({
      selectedItemId: selectedPriority,
      callback,
      loadData: async () => priorityOptions,
      showSearch: false,
      event,
      side,
    });
  }

  public async externalTasks({
    callback,
    event,
    selectedOption,
    side,
  }: {
    callback: (id: string, user: any) => void;
    event: any;
    selectedOption?: string;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedOption,
      showImages: false,
      showSearch: false,
      callback,
      side,
      loadData: async () => {
        return EXTERNAL_TASK_FILTER_OPTIONS;
      },
    });
  }

  public async projectMemberRoles({
    callback,
    event,
    selectedRole,
    includeOwner = false,
    side,
  }: {
    callback: (id: ProjectMemberRole, user: any) => void;
    event: any;
    selectedRole?: string;
    includeOwner?: boolean;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedRole,
      showImages: false,
      showSearch: false,
      callback,
      side,
      loadData: async () => {
        return PROJECT_MEMBER_ROLE_OPTIONS.filter((role) => includeOwner || role.id !== PROJECT_MEMBER_ROLES.OWNER);
      },
    });
  }

  public async tenantUserRoles({
    callback,
    event,
    selectedRole,
    includeOwner = false,
    side,
  }: {
    callback: (id: ProjectMemberRole, user: any) => void;
    event: any;
    selectedRole?: string;
    includeOwner?: boolean;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedRole,
      showImages: false,
      showSearch: false,
      callback,
      side,
      loadData: async () => {
        return TENANT_USER_ROLE_OPTIONS.filter((role) => includeOwner || role.id !== TENANT_USER_ROLES.OWNER);
      },
    });
  }

  public async sourceTypes({
    callback,
    event,
    selectedSourceTypeId,
    side,
  }: {
    callback: (id: string, user: any) => void;
    event: any;
    selectedSourceTypeId?: string;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedSourceTypeId,
      showImages: false,
      showSearch: false,
      callback,
      side,
      loadData: async () => {
        return [
          {
            title: 'Slack',
            id: 'slack',
          },
          {
            title: 'Email',
            id: 'email',
          },
          {
            title: 'In-app',
            id: 'widget',
          },
          {
            title: 'Dashboard',
            id: 'dashboard',
          },
          {
            title: 'Microsoft Teams',
            id: 'microsoft-teams',
          },
        ];
      },
    });
  }

  public async collaboratorTypes({
    callback,
    event,
    selectedCollaboratorTypeId,
    where = [],
  }: {
    callback: (id: string, name: string, user: any) => void;
    event: any;
    selectedCollaboratorTypeId?: string;
    where?: WhereClause[];
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedCollaboratorTypeId,
      showImages: false,
      showSearch: true,
      callback,
      loadData: async (searchText: string) => {
        const fullWhere: WhereClause[] = [...where];

        const response = await this.collaboratorTypesService.list({
          where: fullWhere,
          limit: 100,
          search: searchText || undefined,
          order: ['createdAt'],
          descending: true,
        });
        return response.data.map((collaboratorType: CollaboratorType) => {
          return {
            title: collaboratorType.name,
            id: collaboratorType.id,
            object: collaboratorType,
          };
        });
      },
    });
  }

  public async conversations({
    callback,
    event,
    customerId,
    where = [],
    projectId,
  }: {
    callback: (id: string, _name: string, conversation: any) => void;
    event: any;
    customerId?: string;
    where?: WhereClause[];
    projectId?: string;
  }) {
    this.showPopover({
      event,
      showImages: false,
      showSearch: true,
      callback,
      loadData: async (searchText: string) => {
        const fullWhere: WhereClause[] = [...where];

        if (customerId) {
          fullWhere.push({
            field: 'customerId',
            operator: '==',
            value: customerId,
          });
        }

        if (projectId) {
          fullWhere.push({
            field: 'projectId',
            operator: '==',
            value: projectId,
          });
        }

        const response = await this.conversationsService.list({
          where: fullWhere,
          limit: 100,
          search: searchText || undefined,
          order: ['createdAt'],
          descending: true,
        });

        return response.data.map((conversation: Conversation) => {
          return {
            title: `[${conversation.friendlyId}] ${conversation.title}`,
            id: conversation.id,
            object: conversation,
          };
        });
      },
    });
  }

  public async templates({
    callback,
    event,
    selectedTemplateId,
    side,
    templateType,
    componentView,
    showNoneOption = true,
    projectId,
  }: {
    callback: (id: string, title: string, template: any) => void;
    event: any;
    selectedTemplateId?: string;
    side?: PopoverSide;
    templateType?: 'slack' | 'email';
    componentView?: string;
    showNoneOption?: boolean;
    projectId?: string | null;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedTemplateId,
      showImages: false,
      showSearch: true,
      side,
      placeholderLink: `/${routes.DASHBOARD}/${routes.OUTBOUND}/templates`,
      componentView,
      callback,
      loadData: async (searchText: string) => {
        const params: any = templateType
          ? {
              type: templateType,
            }
          : {};

        if (projectId) {
          params.projectId = [projectId, null];
        } else if (projectId === null) {
          params.projectId = null;
        }

        const response: any = await this.apiService.getPromise(`/templates`, params);
        const data = [];

        if (showNoneOption && (!searchText?.length || NONE_TEXT.toLowerCase().includes(searchText.toLowerCase()))) {
          data.push({
            title: NONE_TEXT,
            id: null,
            image: environment.assetsBaseUrl + '/imgs/1x1.png',
            object: {},
          });
        }

        response?.rows.map((template: Template) => {
          if (!searchText?.length || template.name.toLowerCase().includes(searchText.trim().toLowerCase())) {
            data.push({
              title: template.name,
              id: template.id,
              object: template,
            });
          }
        });
        return data;
      },
    });
  }

  public async channelMembers({
    callback,
    event,
    customerId,
    side,
  }: {
    callback: (id: string, name: string, member: any) => void;
    event: any;
    customerId: string;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      showImages: true,
      showSearch: false,
      callback,
      side,
      loadData: async (_searchText: string) => {
        const response: any = await this.apiService.getPromise(`/customers/${customerId}/members`);

        return response?.map((member: any) => {
          return {
            title: member.user.name,
            id: member.user.id,
            image: member.user.photo,
            object: member,
          };
        });
      },
    });
  }

  public async allUsers({
    callback,
    event,
    selectedUserId,
    side,
    dynamicCreationCallback,
    showCurrentUserOption = false,
  }: {
    callback: (id: string, name: string, user: any) => void;
    event: any;
    selectedUserId?: string;
    side?: PopoverSide;
    dynamicCreationCallback?: (searchText: string) => void;
    showCurrentUserOption?: boolean;
  }) {
    this.showPopover({
      event,
      showImages: true,
      showSearch: true,
      side,
      selectedItemId: selectedUserId,
      callback,
      dynamicCreationCallback,
      initialItems: [
        ...(showCurrentUserOption
          ? [
              {
                id: CURRENT_USER_SENTINEL,
                title: 'Current User',
                icon: 'person-circle-outline',
                image: undefined,
                object: { id: CURRENT_USER_SENTINEL, name: 'Current User' },
              },
            ]
          : []),
      ],
      loadData: async (searchText: string) => {
        const where: WhereClause[] = [
          {
            field: 'name',
            operator: '!=',
            value: null,
          },
        ];

        // Determine which fields to search based on search text
        let searchFields: string[] = [];
        if (searchText?.trim().length) {
          searchFields.push('name');
          if (searchText.includes('@')) {
            searchFields.push('email');
          }
        }

        const result = await this.usersService.list({
          limit: 100,
          includeExternalUsers: true,
          where,
          order: ['name', 'email'],
          descending: false,
          searchText: searchText?.trim() || undefined,
          searchFields: searchFields.length > 0 ? searchFields : undefined,
        });

        const data = [];

        const connectedTeams = await this.slackService.getConnectedTeams();

        // Client-side filtering and display formatting for email matches
        const searchTerm = searchText?.toLowerCase().trim() || '';

        result.data.forEach((user) => {
          // Check if search text matches email for display formatting
          const emailMatched = searchTerm && user.email?.toLowerCase().includes(searchTerm);
          const nameMatched = searchTerm && user.name?.toLowerCase().includes(searchTerm);

          // If search matches email and not name, show "Name (email)"
          const formattedUserName = formatUserName(user, connectedTeams);
          const displayTitle =
            emailMatched && !nameMatched && user.email ? `${formattedUserName} (${user.email})` : formattedUserName;

          data.push({
            title: displayTitle,
            id: user.id,
            image: user.photo,
            object: user,
          });
        });
        return data;
      },
    });
  }

  public async ticketTypes({
    callback,
    event,
    selectedTicketTypeId,
    showNoneOption = true,
    projectId,
    side,
  }: {
    callback: (id: string, title, data: TicketType) => void;
    event: any;
    selectedTicketTypeId?: string;
    showNoneOption?: boolean;
    side?: PopoverSide;
    projectId?: string;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedTicketTypeId,
      showImages: false,
      showSearch: true,
      side,
      placeholderLink: `/${routes.DASHBOARD}/${routes.SETTINGS}/${routes.TICKET_TYPES}`,
      callback,
      loadData: async (searchText: string) => {
        const where: WhereClause[] = [];

        if (projectId) {
          where.push({
            field: 'projectId',
            operator: '==',
            value: projectId,
          });
        }

        // Don't filter by search text on backend - we'll do client-side filtering
        // This ensures we get all ticket types and can search aliases properly
        const response = await this.ticketTypesService.list({
          where,
          limit: 100,
          order: ['name'],
          descending: false,
          search: searchText,
        });

        const data = [];

        if (showNoneOption && (!searchText?.length || NONE_TEXT.toLowerCase().includes(searchText.toLowerCase()))) {
          data.push({
            title: NONE_TEXT,
            id: null,
            image: environment.assetsBaseUrl + '/imgs/1x1.png',
            object: {},
          });
        }

        // Client-side filtering following the same pattern as TicketTypesListComponent
        const searchTerm = searchText?.toLowerCase().trim() || '';

        response.data.forEach((ticketType: TicketType) => {
          // Check if search text matches any alias for display formatting
          const matchedAlias = ticketType.aliases?.find((alias) => alias.toLowerCase().includes(searchTerm));

          // If search matches an alias and the alias is different from the name, show "alias (name)"
          const displayTitle =
            matchedAlias && matchedAlias.toLowerCase() !== ticketType.name.toLowerCase() && searchTerm
              ? `${matchedAlias} (${ticketType.name})`
              : ticketType.name;

          data.push({
            title: displayTitle,
            id: ticketType.id,
            object: ticketType,
          });
        });

        return data;
      },
    });
  }

  public async projects({
    callback,
    event,
    selectedProjectId,
    showNoneOption = true,
    showImages = false,
    noneOptionLabel,
    side,
    ownerOnly = false,
  }: {
    callback: (id: string, title, data: Project) => void;
    event: any;
    selectedProjectId?: string;
    showNoneOption?: boolean;
    showImages?: boolean;
    noneOptionLabel?: string;
    side?: PopoverSide;
    ownerOnly?: boolean;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedProjectId,
      showImages,
      showSearch: true,
      side,
      placeholderLink: `/${routes.DASHBOARD}/${routes.SETTINGS}/${routes.PROJECTS}`,
      noneOptionLabel,
      callback,
      loadData: async (searchText: string) => {
        const where: WhereClause[] = [];

        if (searchText?.length) {
          where.push({
            field: 'name',
            operator: 'like',
            value: `%${searchText}%`,
          });
        }

        const response = await this.projectsService.list({
          where,
          limit: 100,
          order: ['name'],
          descending: false,
        });

        const data = [];

        if (showNoneOption && (!searchText?.length || NONE_TEXT.toLowerCase().includes(searchText.toLowerCase()))) {
          data.push({
            title: noneOptionLabel || NONE_TEXT,
            id: null,
            image: environment.assetsBaseUrl + '/imgs/1x1.png',
            object: {},
          });
        }

        response.data
          .filter((item) => {
            if (ownerOnly) {
              return this.authService.isProjectOwner(item.id);
            }

            return true;
          })
          .forEach((item) => {
            data.push({
              title: item.name,
              id: item.id,
              object: item,
              image: item.supportLogo,
            });
          });

        return data;
      },
    });
  }

  public async automations({
    callback,
    event,
    selectedAutomationId,
    showNoneOption,
    trigger,
    side,
    showUnlisted = true,
    projectId,
  }: {
    callback: (id: string, name: string, data: Automation) => void;
    event: any;
    selectedAutomationId?: string;
    showNoneOption?: boolean;
    side?: PopoverSide;
    trigger?: {
      type?: string | string[];
      objectType?: string | string[];
      event?: string | string[];
    };
    showUnlisted?: boolean;
    projectId?: string | null;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedAutomationId,
      showImages: false,
      showSearch: true,
      side,
      placeholderLink: `/${routes.DASHBOARD}/${routes.SETTINGS}/${routes.AUTOMATIONS}`,
      callback,
      loadData: async (searchText: string) => {
        const where: WhereClause[] = [];

        if (projectId) {
          where.push({
            field: 'projectId',
            operator: 'in',
            value: [projectId, null], // Fetch both project and global automations.
          });
        } else if (projectId === null) {
          where.push({
            field: 'projectId',
            operator: '==',
            value: null,
          });
        }

        if (searchText?.length) {
          where.push({
            field: 'name',
            operator: 'like',
            value: `%${searchText}%`,
          });
        }

        if (trigger?.type) {
          if (Array.isArray(trigger.type)) {
            where.push({
              field: 'trigger.type',
              operator: 'in',
              value: trigger.type,
            });
          } else {
            where.push({
              field: 'trigger.type',
              operator: '==',
              value: trigger.type,
            });
          }
        }

        if (trigger?.objectType) {
          if (Array.isArray(trigger.objectType)) {
            where.push({
              field: 'trigger.data.objectType',
              operator: 'in',
              value: trigger.objectType,
            });
          } else {
            where.push({
              field: 'trigger.data.objectType',
              operator: '==',
              value: trigger.objectType,
            });
          }
        }

        if (trigger?.event) {
          if (Array.isArray(trigger.event)) {
            where.push({
              field: 'trigger.data.event',
              operator: 'in',
              value: trigger.event,
            });
          } else {
            where.push({
              field: 'trigger.data.event',
              operator: '==',
              value: trigger.event,
            });
          }
        }

        if (!showUnlisted) {
          where.push({
            field: 'trigger.data.isUnlisted',
            operator: '!=',
            value: true,
          });
        }

        const response = await this.automationsService.list({
          where,
          limit: null,
          order: ['name'],
          descending: false,
        });

        const data = [];

        if (showNoneOption && (!searchText?.length || NONE_TEXT.toLowerCase().includes(searchText.toLowerCase()))) {
          data.push({
            title: NONE_TEXT,
            id: null,
            image: environment.assetsBaseUrl + '/imgs/1x1.png',
            object: {},
          });
        }

        response.data.forEach((automation: Automation) => {
          data.push({
            title: automation.name,
            id: automation.id,
            object: automation,
          });
        });

        return data;
      },
    });
  }

  public async slas({
    callback,
    event,
    selectedSlaId,
    side,
  }: {
    callback: (id: string, title: string, sla: any) => void;
    event: any;
    selectedSlaId?: string;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedSlaId,
      showImages: false,
      showSearch: false,
      side,
      callback,
      loadData: async (_searchText: string) => {
        const result: any = await this.apiService.postPromise('/support-step-rulesets/list', {
          order: ['createdAt'],
          descending: false,
        });

        const data = [
          {
            title: 'Default',
            id: null,
            object: null,
          },
        ];

        result.data.forEach((sla) => {
          data.push({
            title: sla.name,
            id: sla.id,
            object: sla,
          });
        });

        return data;
      },
    });
  }

  public async externalCrmAccounts({
    callback,
    event,
    selectedAccountId,
    initialSearchText,
    side,
  }: {
    callback: (id: string, title: string, sla: any) => void;
    event: any;
    selectedAccountId?: string;
    side?: PopoverSide;
    initialSearchText?: string;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedAccountId,
      showImages: false,
      showSearch: true,
      side,
      initialSearchText,
      callback,
      loadData: async (searchText: string) => {
        const where: WhereClause[] = [];

        const options: any = {
          limit: 100,
          order: ['name'],
        };

        if (searchText) {
          options.where = [
            {
              field: 'name',
              operator: 'like',
              value: `%${searchText}%`,
            },
          ];
        }

        const response: ListResponse<ExternalCrmAccount> = await this.apiService.postPromise(
          '/external-crm/accounts/list',
          options,
        );

        const data = [];

        response.data.forEach((account: ExternalCrmAccount) => {
          data.push({
            title: account.name,
            id: account.id,
            object: account,
          });
        });

        return data;
      },
    });
  }

  public async serviceAccounts({
    callback,
    event,
    selectedServiceAccountId,
    showNoneOption = true,
    side,
  }: {
    callback: (id: string, title, data: TicketType) => void;
    event: any;
    selectedServiceAccountId?: string;
    showNoneOption?: boolean;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedServiceAccountId,
      showImages: false,
      showSearch: true,
      side,
      placeholderLink: `/${routes.DASHBOARD}/${routes.ADMIN}/${routes.SERVICE_ACCOUNTS}`,
      callback,
      loadData: async (searchText: string) => {
        const where: WhereClause[] = [];

        if (searchText?.length) {
          where.push({
            field: 'name',
            operator: 'like',
            value: `%${searchText}%`,
          });
        }

        const response = await this.serviceAccountsService.list({
          where,
          limit: 100,
          order: ['name'],
          descending: false,
        });

        const data = [];

        if (showNoneOption && (!searchText?.length || NONE_TEXT.toLowerCase().includes(searchText.toLowerCase()))) {
          data.push({
            title: NONE_TEXT,
            id: null,
            image: environment.assetsBaseUrl + '/imgs/1x1.png',
            object: {},
          });
        }

        response.data.forEach((item) => {
          data.push({
            title: item.name,
            id: item.id,
            object: item,
          });
        });

        return data;
      },
    });
  }

  public async slackChannels({
    callback,
    event,
    selectedSlackChannelId,
    side,
  }: {
    callback: (id: string, name: string, data: Automation) => void;
    event: any;
    selectedSlackChannelId?: string;
    showNoneOption?: boolean;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedSlackChannelId,
      showImages: false,
      showSearch: true,
      side,
      callback,
      loadData: async (searchText: string) => {
        const { channels } = await this.slackService.getConnectedChannels(true);

        return channels
          .filter((channel) => !searchText || channel.name.toLowerCase().includes(searchText.toLowerCase()))
          .map((channel) => ({
            title: channel.name,
            id: channel.id,
            object: channel,
          }));
      },
    });
  }

  public async slackTeams({
    callback,
    event,
    selectedSlackTeamId,
    side,
  }: {
    callback: (id: string, name: string, data: Automation) => void;
    event: any;
    selectedSlackTeamId?: string;
    showNoneOption?: boolean;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedSlackTeamId,
      showImages: false,
      showSearch: true,
      side,
      callback,
      loadData: async (searchText: string) => {
        const teamsResponse: ListResponse<{
          id: string;
          name: string;
          icon: string;
        }> = await this.apiService.postPromise('/slack-teams/list', {
          limit: 100,
          order: ['name'],
          descending: false,
        });

        return teamsResponse.data
          .filter((team) => !searchText || team.name.toLowerCase().includes(searchText.toLowerCase()))
          .map((team) => ({
            title: team.name,
            id: team.id,
            object: team,
          }));
      },
    });
  }

  public async slackUserGroups({
    callback,
    event,
    selectedSlackGroupId,
    excludeManagedByUnthread = false,
    showNoneOption,
    side,
  }: {
    callback: (id: string, name: string, data: Automation) => void;
    event: any;
    selectedSlackGroupId?: string;
    excludeManagedByUnthread?: boolean;
    showNoneOption?: boolean;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedSlackGroupId,
      showImages: false,
      showSearch: true,
      side,
      callback,
      loadData: async (searchText: string) => {
        const commonListOptions = {
          limit: 100,
          order: ['name'],
          descending: false,
        };

        const where: WhereClause[] = excludeManagedByUnthread
          ? [{ field: 'managedByUnthread', operator: '==', value: false }]
          : [];

        let fetchedGroups: SlackGroup[] = [];

        if (searchText?.length) {
          const nameQueryWhere: WhereClause[] = where.concat([
            {
              field: 'name',
              operator: 'like',
              value: `%${searchText}%`,
            },
          ]);
          const slackHandleQueryWhere: WhereClause[] = where.concat([
            {
              field: 'slackHandle',
              operator: 'like',
              value: `%${searchText}%`,
            },
          ]);

          // We do this instead of filtering after the list (as we do in other methods) as there may be 1000s of user groups
          const [nameResponse, slackHandleResponse] = await Promise.all([
            this.slackUserGroupsService.list({
              ...commonListOptions,
              where: nameQueryWhere,
            }),
            this.slackUserGroupsService.list({
              ...commonListOptions,
              where: slackHandleQueryWhere,
            }),
          ]);

          const combinedRawGroups = (nameResponse.data as SlackGroup[]).concat(
            slackHandleResponse.data as SlackGroup[],
          );

          // Remove duplicates
          fetchedGroups = Array.from(new Set(combinedRawGroups));
        } else {
          const response = await this.slackUserGroupsService.list({
            ...commonListOptions,
            where,
          });
          fetchedGroups = response.data as SlackGroup[];
        }

        const data = [];

        if (showNoneOption && (!searchText?.length || NONE_TEXT.toLowerCase().includes(searchText.toLowerCase()))) {
          data.push({
            title: NONE_TEXT,
            id: null,
            image: environment.assetsBaseUrl + '/imgs/1x1.png',
            object: {},
          });
        }

        fetchedGroups.forEach((item: SlackGroup) => {
          data.push({
            title: `${item.name} - @${item.slackHandle}`,
            id: item.slackId,
            object: item,
          });
        });

        return data;
      },
    });
  }

  public async scimGroups({
    callback,
    event,
    selectedScimGroupId,
    showNoneOption,
    side,
  }: {
    callback: (id: string, name: string, data: Automation) => void;
    event: any;
    selectedScimGroupId?: string;
    showNoneOption?: boolean;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedScimGroupId,
      showImages: false,
      showSearch: true,
      side,
      callback,
      loadData: async (searchText: string) => {
        const response: any[] = await this.apiService.getPromise('/scim-groups');
        const data = [];

        if (showNoneOption && (!searchText?.length || NONE_TEXT.toLowerCase().includes(searchText.toLowerCase()))) {
          data.push({
            title: NONE_TEXT,
            id: null,
            image: environment.assetsBaseUrl + '/imgs/1x1.png',
            object: {},
          });
        }

        response
          .filter((item: any) => !searchText || item.name.toLowerCase().includes(searchText.toLowerCase()))
          .forEach((item: any) => {
            data.push({
              title: item.name,
              id: item.idpId,
              object: item,
            });
          });

        return data;
      },
    });
  }

  public async dynamicUserFieldValues({
    callback,
    event,
    selectedDynamicUserFieldValueId,
    showNoneOption,
    field,
    side,
  }: {
    callback: (id: string, name: string, data: Automation) => void;
    event: any;
    selectedDynamicUserFieldValueId?: string;
    field: string;
    showNoneOption?: boolean;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedDynamicUserFieldValueId,
      showImages: false,
      showSearch: true,
      side,
      callback,
      loadData: async (searchText: string) => {
        const response: any[] = await this.apiService.postPromise('/users/field-values', {
          field,
          search: searchText,
        });
        const data = [];

        if (showNoneOption && (!searchText?.length || NONE_TEXT.toLowerCase().includes(searchText.toLowerCase()))) {
          data.push({
            title: NONE_TEXT,
            id: null,
            image: environment.assetsBaseUrl + '/imgs/1x1.png',
            object: {},
          });
        }

        response.forEach((item: any) => {
          data.push({
            title: item,
            id: item,
            object: item,
          });
        });

        return data;
      },
    });
  }

  public async entities({
    callback,
    event,
    selectedEntityId,
    includeServiceAccounts = false,
    type,
    isExternal,
    side,
    excludeIds,
    showNoneOption = false,
    dynamicCreationCallback,
  }: {
    callback: (id: string, name: string, user: any) => void;
    event?: any;
    selectedEntityId?: string;
    includeServiceAccounts?: boolean;
    isExternal?: boolean;
    type?: string;
    side?: PopoverSide;
    dynamicCreationCallback?: (searchText: string) => void;
    excludeIds?: string[];
    requireSlackId?: boolean;
    showNoneOption?: boolean;
  }) {
    this.showPopover({
      event,
      showImages: true,
      showSearch: true,
      side,
      selectedItemId: selectedEntityId,
      callback,
      dynamicCreationCallback,
      loadData: async (searchText: string) => {
        const where: WhereClause[] = [
          {
            field: 'name',
            operator: '!=',
            value: null,
          },
        ];

        // Determine which fields to search based on search text
        let searchFields: string[] = [];
        if (searchText?.trim().length) {
          searchFields.push('name');
          if (searchText.includes('@')) {
            searchFields.push('email');
          }
        }

        if (!includeServiceAccounts) {
          where.push({
            field: 'isServiceAccount',
            operator: '!=',
            value: true,
          });
        }

        if (isExternal != null) {
          where.push({
            field: 'isExternal',
            operator: '==',
            value: isExternal,
          });
        }

        if (type) {
          where.push({
            field: 'type',
            operator: '==',
            value: type,
          });
        }

        if (excludeIds?.length) {
          where.push({
            field: 'id',
            operator: 'notIn',
            value: excludeIds,
          });
        }

        const result: ListResponse<Entity> = await this.apiService.postPromise('/entities/list', {
          limit: 100,
          where,
          order: ['type', 'name', 'email'],
          descending: false,
          searchText: searchText?.trim() || undefined,
          searchFields: searchFields.length > 0 ? searchFields : undefined,
        });

        const data = [];

        if (showNoneOption) {
          data.push({
            title: 'None',
            id: null,
            image: environment.assetsBaseUrl + '/imgs/1x1.png',
            object: {},
          });
        }

        // Client-side filtering and display formatting for email matches
        const searchTerm = searchText?.toLowerCase().trim() || '';

        result.data.forEach((entity) => {
          // Check if search text matches email for display formatting
          const emailMatched = searchTerm && entity.email?.toLowerCase().includes(searchTerm);
          const nameMatched = searchTerm && entity.name?.toLowerCase().includes(searchTerm);

          // If search matches email and not name, show "Name (email)"
          const displayTitle =
            emailMatched && !nameMatched && entity.email ? `${entity.name} (${entity.email})` : entity.name;

          data.push({
            title: displayTitle,
            id: entity.id,
            image: entity.photo,
            object: entity,
          });
        });
        return data;
      },
    });
  }

  public async customFieldOptions({
    callback,
    event,
    field,
    context,
    contextOverrides,
    selectedValue,
    side,
    dynamicCreationCallback,
    showNoneOption = false,
  }: {
    callback: (id: string, name: string, user: any) => void;
    event: any;
    field: any;
    context: any;
    contextOverrides?: any;
    selectedValue?: string;
    side?: PopoverSide;
    dynamicCreationCallback?: (searchText: string) => void;
    showNoneOption?: boolean;
  }) {
    this.showPopover({
      event,
      showImages: false,
      showSearch: true,
      side,
      selectedItemId: selectedValue,
      callback,
      dynamicCreationCallback,
      loadData: async (searchText: string) => {
        try {
          // Use the new consolidated endpoint that handles all field types
          const options = await this.customFieldsService.getOptions(field.id, {
            query: searchText?.trim(),
            context,
            contextOverrides,
          });

          if (showNoneOption) {
            options.unshift({ label: 'None', value: null });
          }

          return options.map((option) => {
            return {
              title: option.label,
              id: option.value,
              object: { label: option.label, id: option.value },
            };
          });
        } catch (error) {
          console.error('Failed to get custom field options', error);
          // Return empty array on error to avoid breaking the UI
          return [];
        }
      },
    });
  }

  public async customFields({
    callback,
    event,
    selectedValue,
    side,
    dynamicCreationCallback,
    dynamicCreationCallbackLabel,
    projectId,
    excludeIds,
    allowedTypes,
    showIcon = false,
  }: {
    callback: (id: string, name: string, user: any) => void;
    event: any;
    selectedValue?: string;
    side?: PopoverSide;
    dynamicCreationCallback?: (searchText: string) => void;
    dynamicCreationCallbackLabel?: string;
    projectId?: string;
    excludeIds?: string[];
    allowedTypes?: string[];
    showIcon?: boolean;
  }) {
    this.showPopover({
      event,
      showImages: false,
      showSearch: true,
      side,
      selectedItemId: selectedValue,
      callback,
      dynamicCreationCallback,
      dynamicCreationCallbackLabel,
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim();
        const where: WhereClause[] = [];

        if (allowedTypes?.length) {
          where.push({ field: 'type', operator: 'in', value: allowedTypes });
        }

        if (projectId) {
          where.push({ field: 'projectId', operator: '==', value: projectId });
        }

        if (excludeIds?.length) {
          where.push({ field: 'id', operator: 'notIn', value: excludeIds });
        }

        const ticketTypesResponse = (await this.customFieldsService.list({
          where,
          order: ['label'],
          descending: false,
        })) as ListResponse<CustomField>;

        // Filter ticket types by the existence of custom fields and return all custom fields as options
        return ticketTypesResponse.data
          .map((f) => {
            return {
              title: `${f.ticketType?.name ? `[${f.ticketType.name}] ` : ''}${f.label}${showIcon ? ` ${f.type === CUSTOM_FIELD_TYPES.USER_SELECT ? '👤' : f.type === CUSTOM_FIELD_TYPES.USER_GROUP_SELECT ? '👥' : ''}` : ''}`,
              id: f.id,
              object: f,
            };
          })
          .filter((f) => !cleanQuery || f.title.toLowerCase().includes(cleanQuery.toLowerCase()));
      },
    });
  }

  public async externalSelectFunctions({
    callback,
    event,
    selectedValue,
    side,
    dynamicCreationCallback,
    projectId,
  }: {
    callback: (id: string, name: string, user: any) => void;
    event: any;
    selectedValue?: string;
    side?: PopoverSide;
    dynamicCreationCallback?: (searchText: string) => void;
    projectId?: string | null;
  }) {
    this.showPopover({
      event,
      showImages: false,
      showSearch: true,
      side,
      selectedItemId: selectedValue,
      callback,
      dynamicCreationCallback,
      loadData: async (searchText: string) => {
        const [automationFunctions, tenantAutomationFunctions]: [any, any] = await Promise.all([
          projectId
            ? this.apiService.getPromise('/automations/functions/active', {
                projectId,
              })
            : {},
          this.apiService.getPromise('/automations/functions/active'),
        ]);

        const allAutomationFunctions = [
          ...(tenantAutomationFunctions?.exportedFunctionsByType?.externalSelect ?? []).map((functionName: string) => [
            null,
            functionName,
          ]),
          ...(automationFunctions?.exportedFunctionsByType?.externalSelect ?? []).map((functionName: string) => [
            projectId,
            functionName,
          ]),
        ];

        return (
          allAutomationFunctions.flatMap((functionName) => {
            const [resolvedProjectId, resolvedFunctionName] = functionName;

            if (searchText && !resolvedFunctionName.includes(searchText)) {
              return [];
            }

            return [
              {
                title: resolvedFunctionName,
                id: functionName.join('/'),
                object: {
                  label: resolvedFunctionName,
                  projectId: resolvedProjectId,
                },
              },
            ];
          }) ?? []
        );
      },
    });
  }

  public async accounts({
    callback,
    event,
    selectedAccountId,
    side,
  }: {
    callback: (id: string, name: string, user: any) => void;
    event: any;
    selectedAccountId?: string;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedAccountId,
      showImages: false,
      showSearch: true,
      side,
      callback,
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim();

        const accounts: ListResponse<Account> = await this.apiService.postPromise('/accounts/list', {
          where: cleanQuery ? [{ field: 'name', operator: 'like', value: `%${cleanQuery}%` }] : [],
        });

        return accounts.data.map((account) => {
          return {
            title: account.name,
            id: account.id,
            object: account,
          };
        });
      },
    });
  }

  public async accountCustomFields({
    callback,
    event,
    selectedFieldId,
    side,
  }: {
    callback: (id: string, name: string, field: CustomField) => void;
    event: any;
    selectedFieldId?: string;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedFieldId,
      showImages: false,
      showSearch: true,
      side,
      callback,
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim();
        const customFields = this.authService.tenant.customCustomerFields ?? [];

        const allowedFieldTypes = [
          CUSTOM_FIELD_TYPES.SHORT_ANSWER,
          CUSTOM_FIELD_TYPES.LONG_ANSWER,
          CUSTOM_FIELD_TYPES.CHECKBOX,
          CUSTOM_FIELD_TYPES.SINGLE_SELECT,
          CUSTOM_FIELD_TYPES.MULTI_SELECT,
          CUSTOM_FIELD_TYPES.DATE,
          CUSTOM_FIELD_TYPES.TEXT,
        ];

        return customFields
          .filter(
            (field) =>
              allowedFieldTypes.includes(field.type) &&
              (!cleanQuery || field.label.toLowerCase().includes(cleanQuery.toLowerCase())),
          )
          .map((field) => ({
            title: field.label,
            id: field.id,
            object: field,
          }));
      },
    });
  }

  public async dateValues({
    callback,
    event,
    selectedDateValueId,
    side,
  }: {
    callback: (id: string, name: any, object: any) => void;
    event: any;
    selectedDateValueId?: string;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedDateValueId,
      showImages: false,
      showSearch: true,
      callback,
      side,
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim();
        return DATE_OPTIONS.filter((dateOption) => {
          return !cleanQuery || dateOption.label.toLowerCase().includes(cleanQuery.toLowerCase());
        }).map((dateOption) => {
          return {
            title: dateOption.label,
            id: dateOption.label,
            object: dateOption,
          };
        });
      },
    });
  }

  public async inboxViews({
    callback,
    event,
    selectedInboxViewId,
    side,
  }: {
    callback: (id: string, name: string, user: any) => void;
    event: any;
    selectedInboxViewId?: string;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedInboxViewId,
      showImages: false,
      showSearch: true,
      callback,
      side,
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim();
        const savedFilters: Partial<SavedFilter>[] = await this.apiService.getPromise('/saved-filters?view=inbox');

        const transformedSavedFilters = savedFilters.map((savedFilter) => {
          return {
            title: savedFilter.name,
            id: `${PATHS.INBOX}/filter/${savedFilter.id}`,
            object: savedFilter,
          };
        });

        const staticSubpages = getInboxSubpages(this.authService.tenant.featureFlags?.escalationsEnabled).map(
          (subpage) => {
            return {
              title: subpage.title,
              id: subpage.url,
              object: subpage,
            };
          },
        );

        return [...staticSubpages, ...transformedSavedFilters].filter((subpage) => {
          return !cleanQuery || subpage.title.toLowerCase().includes(cleanQuery.toLowerCase());
        });
      },
    });
  }

  public async savedReports({
    callback,
    event,
    selectedReportId,
    side,
  }: {
    callback: (id: string, name: string, report: SavedReport) => void;
    event: any;
    selectedReportId?: string;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedReportId,
      showImages: false,
      showSearch: true,
      side,
      callback,
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim();

        const response: SavedReport[] = await this.apiService.getPromise('/saved-reports');

        return response
          .filter((report: SavedReport) => {
            return !cleanQuery || report.name.toLowerCase().includes(cleanQuery.toLowerCase());
          })
          .map((report: SavedReport) => {
            return {
              title: report.name,
              id: report.id,
              object: report,
            };
          });
      },
    });
  }

  public async subStatuses({
    callback,
    event,
    selectedSubStatusId,
    showNoneOption,
    side,
    type,
    projectId,
  }: {
    callback: (id: string, name: string, user: any) => void;
    event: any;
    selectedSubStatusId?: string;
    showNoneOption?: boolean;
    side?: PopoverSide;
    type?: string;
    projectId?: string;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedSubStatusId,
      showImages: false,
      showSearch: true,
      callback,
      side,
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim();
        const subStatuses: ListResponse<SubStatus> = await this.apiService.postPromise('/sub-statuses/list', {
          where: [
            cleanQuery ? { field: 'name', operator: 'like', value: `%${cleanQuery}%` } : undefined,
            type ? { field: 'type', operator: '==', value: type } : undefined,
            projectId ? { field: 'projectId', operator: '==', value: projectId } : undefined,
          ].filter(Boolean),
        });

        const data = [];

        if (showNoneOption && (!searchText?.length || NONE_TEXT.toLowerCase().includes(searchText.toLowerCase()))) {
          data.push({
            title: NONE_TEXT,
            id: null,
            image: '',
            object: {},
          });
        }

        subStatuses.data.map((subStatus) => {
          const type = subStatus.key.split('__')[0];

          data.push({
            title: subStatus.name,
            id: subStatus.id,
            icon: STATUS_COLUMN_FORMAT_MAP[type]?.icon,
            iconClass: STATUS_COLUMN_FORMAT_MAP[type]?.iconClass,
            object: subStatus,
          });
        });

        return data;
      },
    });
  }

  public async subStatusKeys({
    callback,
    event,
    selectedSubStatusKey,
    includeIntake,
    showNoneOption,
    type,
    side,
    projectId,
  }: {
    callback: (id: string, name: string, user: any) => void;
    event: any;
    selectedSubStatusKey?: string;
    showNoneOption?: boolean;
    side?: PopoverSide;
    type?: string;
    includeIntake?: boolean;
    projectId?: string;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedSubStatusKey,
      showImages: false,
      showSearch: true,
      callback,
      side,
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim();
        const subStatusKeys: Partial<SubStatus>[] = await this.apiService.getPromise('/sub-statuses/by-key', {
          includeIntake,
          projectId,
        });

        const data = [];

        if (showNoneOption && (!searchText?.length || NONE_TEXT.toLowerCase().includes(searchText.toLowerCase()))) {
          data.push({
            title: NONE_TEXT,
            id: null,
            image: '',
            object: {},
          });
        }

        subStatusKeys
          .filter((subStatus) => !type || subStatus.type === type)
          .filter((subStatus) => !cleanQuery || subStatus.name.toLowerCase().includes(cleanQuery.toLowerCase()))
          .map((subStatus) => {
            data.push({
              title: subStatus.name,
              id: subStatus.key,
              icon: STATUS_COLUMN_FORMAT_MAP[subStatus.type]?.icon,
              iconClass: STATUS_COLUMN_FORMAT_MAP[subStatus.type]?.iconClass,
              object: subStatus,
            });
          });

        return data;
      },
    });
  }

  public async knowledgeBaseScrapeJobs({
    callback,
    event,
    selectedJobId,
    side,
  }: {
    callback: (id: string, title: string, job: KnowledgeBaseScrapeJob) => void;
    event: any;
    selectedJobId?: string;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedJobId,
      showSearch: true,
      showImages: false,
      callback: callback,
      loadData: async (searchText: string) => {
        try {
          const response = await this.knowledgeBaseScrapeJobsService.list({
            limit: 50,
            descending: false,
            select: ['id', 'startUrl', 'sourceType', 'title'],
          });

          const filteredData = response.data.filter(
            (job) => !searchText || job.startUrl?.toLowerCase().includes(searchText.toLowerCase()),
          );

          return filteredData.map((job) => ({
            id: job.id,
            title: job.title || job.startUrl || `Job ${job.id}`,
            subtitle: job.title && job.startUrl ? job.startUrl : undefined,
            object: job,
            icon: mapSourceTypeToIconName(job.sourceType),
          }));
        } catch (error) {
          console.error('Error loading scrape jobs in SearchablePopoverService:', error);
          return [];
        }
      },
      side,
    });
  }

  public async automationActions({
    callback,
    event,
    actionOptions,
    side,
  }: {
    callback: (id: string, title: string, metadata: any) => void;
    event: any;
    actionOptions: any[];
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      showImages: false,
      showSearch: true,
      side,
      callback,
      loadData: async (searchText: string) => {
        const lowerSearchText = searchText?.toLowerCase().trim();

        return actionOptions
          .filter((item) => {
            if (!item || typeof item.label !== 'string') return false;
            if (!lowerSearchText) {
              return true;
            }
            const labelMatch = item.label.toLowerCase().includes(lowerSearchText);
            const descriptionMatch = item.metadata?.description?.toLowerCase().includes(lowerSearchText);
            return labelMatch || descriptionMatch;
          })
          .map((item) => ({
            title: item.label,
            id: item.label,
            icon: item.icon,
            object: item.metadata,
          }));
      },
    });
  }

  public async userGroups({
    callback,
    event,
    selectedGroupId,
    side,
    showNoneOption = true,
    noneOptionLabel = 'No specific group',
    where,
  }: {
    callback: (id: string | null, name: string, group: UserGroup | null) => void;
    event: any;
    selectedGroupId?: string;
    side?: PopoverSide;
    showNoneOption?: boolean;
    noneOptionLabel?: string;
    where?: WhereClause[];
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedGroupId,
      showSearch: true,
      showImages: false,
      noneOptionLabel: showNoneOption ? noneOptionLabel : undefined,
      callback: callback,
      loadData: async (searchText: string) => {
        try {
          const commonListOptions = {
            limit: 100,
            order: ['name'],
            descending: false,
            where: where ?? undefined,
          };

          let response: Partial<UserGroup>[] = [];
          if (searchText?.length) {
            const nameQueryWhere: WhereClause[] = [
              {
                field: 'name',
                operator: 'like',
                value: `%${searchText}%`,
              },
            ];

            response = (
              await this.userGroupsService.list({
                ...commonListOptions,
                where: nameQueryWhere,
              })
            ).data as UserGroup[];
          } else {
            response = (await this.userGroupsService.list(commonListOptions)).data as UserGroup[];
          }

          const data = [];
          if (showNoneOption && (!searchText?.length || NONE_TEXT.toLowerCase().includes(searchText.toLowerCase()))) {
            data.push({
              title: NONE_TEXT,
              id: null,
              image: environment.assetsBaseUrl + '/imgs/1x1.png',
              object: {},
            });
          }

          response.forEach((item: UserGroup) => {
            data.push({
              title: item.name,
              id: item.id,
              object: item,
            });
          });
          return data;
        } catch (error) {
          console.error('Error loading user groups in SearchablePopoverService:', error);
          return [];
        }
      },
      side,
    });
  }

  public async pagerDutyServices({
    callback,
    event,
    selectedServiceId,
    showNoneOption,
    side,
  }: {
    callback: (id: string, name: string) => void;
    event: any;
    selectedServiceId?: string;
    showNoneOption?: boolean;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedServiceId,
      showSearch: true,
      showImages: false,
      callback,
      side,
      loadData: async (searchText: string) => {
        const response: any[] = await this.apiService.getPromise('/pager-duty/services', {
          query: searchText || '',
        });

        const data = [];

        if (showNoneOption && (!searchText?.length || NONE_TEXT.toLowerCase().includes(searchText.toLowerCase()))) {
          data.push({
            title: NONE_TEXT,
            id: null,
            object: {},
          });
        }

        return data.concat(
          response.map((service) => ({
            id: service.id,
            title: service.name,
            object: service,
          })),
        );
      },
    });
  }

  public async pagerDutyEscalationPolicies({
    callback,
    event,
    selectedPolicyId,
    showNoneOption,
    side,
  }: {
    callback: (id: string, name: string) => void;
    event: any;
    selectedPolicyId?: string;
    showNoneOption?: boolean;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedPolicyId,
      showSearch: true,
      showImages: false,
      callback,
      side,
      loadData: async (searchText: string) => {
        const response: any[] = await this.apiService.getPromise('/pager-duty/escalation-policies', {
          query: searchText || '',
        });

        const data = [];

        if (showNoneOption && (!searchText?.length || NONE_TEXT.toLowerCase().includes(searchText.toLowerCase()))) {
          data.push({
            title: NONE_TEXT,
            id: null,
            object: {},
          });
        }

        return data.concat(
          response.map((policy) => ({
            id: policy.id,
            title: policy.name,
            object: policy,
          })),
        );
      },
    });
  }

  public async escalationTypes({
    callback,
    event,
    selectedEscalationTypeId,
    projectId,
    side,
  }: {
    callback: (id: string, name: string, escalationType: EscalationType) => void;
    event: any;
    selectedEscalationTypeId?: string;
    projectId?: string;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedEscalationTypeId,
      showImages: false,
      showSearch: true,
      side,
      callback,
      loadData: async (searchText: string) => {
        try {
          const where: WhereClause[] = [];

          if (projectId) {
            where.push({
              field: 'projectId',
              operator: '==',
              value: projectId,
            });
          }

          const response: any = await this.escalationTypesService.list({
            where,
            limit: 100,
            order: ['name'],
            descending: false,
          });

          const data = [];

          response.data.forEach((escalationType: EscalationType) => {
            if (!searchText?.length || escalationType.name.toLowerCase().includes(searchText.toLowerCase())) {
              data.push({
                title: escalationType.name,
                id: escalationType.id,
                object: escalationType,
              });
            }
          });

          return data;
        } catch (error) {
          console.error('Error loading escalation types:', error);
          return [];
        }
      },
    });
  }

  public async externalTaskManagerUsers({
    callback,
    event,
    selectedUserId,
    side,
  }: {
    callback: (id: string, name: string, user: any) => void;
    event: any;
    selectedUserId?: string;
    side?: PopoverSide;
  }) {
    this.showPopover({
      event,
      selectedItemId: selectedUserId,
      showSearch: true,
      showImages: false,
      callback,
      side,
      loadData: async (searchText: string) => {
        const response: any[] = await this.apiService.postPromise('/external-task-management/get-users', {
          query: searchText || '',
        });

        return response.map((user) => ({
          id: user.id,
          title: user.name,
          object: user,
        }));
      },
    });
  }
}
